import { FC } from 'react';

import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import StarIcon from '@mui/icons-material/Star';
import SearchOffIcon from '@mui/icons-material/SearchOff';

import { ConversationSearchBar } from './ConversationSearchBar';
import { ConversationItem } from './ConversationItem';
import type { ConversationRecord } from '../../types';
import { useConversationHistoryStyles } from './styles/conversation-history.styles';
import { mcpChatTranslationRef } from '../../translation';

interface ConversationHistoryProps {
  /** Starred conversations */
  starredConversations: ConversationRecord[];
  /** Non-starred (recent) conversations */
  recentConversations: ConversationRecord[];
  /** Whether conversations are loading */
  loading: boolean;
  /** Error message if loading failed */
  error?: string;
  /** Current search query */
  searchQuery: string;
  /** Callback to set search query */
  onSearchChange: (query: string) => void;
  /** Callback to clear search */
  onSearchClear: () => void;
  /** Callback when a conversation is selected */
  onSelectConversation: (conversation: ConversationRecord) => void;
  /** Callback to toggle star status */
  onToggleStar: (id: string) => void;
  /** Callback to delete a conversation */
  onDelete: (id: string) => void;
  /** Callback to rename a conversation */
  onRename: (id: string, title: string) => void;
  /** Currently selected conversation ID */
  selectedConversationId?: string;
  /** Whether to focus the search input */
  focusSearch?: boolean;
}

/**
 * Displays a list of conversation history items with search, star, and delete functionality.
 * Shows starred conversations at the top, followed by recent conversations.
 */
export const ConversationHistory: FC<ConversationHistoryProps> = ({
  starredConversations,
  recentConversations,
  loading,
  error,
  searchQuery,
  onSearchChange,
  onSearchClear,
  onSelectConversation,
  onToggleStar,
  onDelete,
  onRename,
  selectedConversationId,
  focusSearch = false,
}) => {
  const classes = useConversationHistoryStyles();
  const { t } = useTranslationRef(mcpChatTranslationRef);

  const hasConversations =
    starredConversations.length > 0 || recentConversations.length > 0;
  const isSearchActive = searchQuery.length >= 2;
  const noSearchResults = isSearchActive && !hasConversations;
  const showSearchBar = hasConversations || isSearchActive || focusSearch;

  return (
    <Box className={classes.root}>
      {/* Search bar */}
      {showSearchBar && (
        <>
          <ConversationSearchBar
            value={searchQuery}
            onChange={onSearchChange}
            onClear={onSearchClear}
            focusOnMount={focusSearch}
          />
          <Divider />
        </>
      )}
      {/* Scrollable content */}
      <Box className={classes.scrollableContent}>
        {/* Loading state */}
        {loading && !hasConversations && (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Error state */}
        {!loading && error && !hasConversations && (
          <Typography
            variant="body2"
            color="error"
            className={classes.errorText}
          >
            {error}
          </Typography>
        )}

        {/* No search results */}
        {noSearchResults && (
          <Box className={classes.noSearchResults}>
            <SearchOffIcon className={classes.noSearchIcon} />
            <Typography variant="body2" color="text.secondary">
              {t('conversationHistory.noResults')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ marginTop: 1 }}
            >
              {t('conversationHistory.noResultsHint')}
            </Typography>
          </Box>
        )}

        {/* Empty state (no search active) */}
        {!hasConversations && !loading && !error && !isSearchActive && (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              {t('conversationHistory.emptyState')}
            </Typography>
            <Typography variant="caption" className="emptyStateHint">
              {t('conversationHistory.emptyStateHint')}
            </Typography>
          </Box>
        )}

        {/* Starred conversations section */}
        {starredConversations.length > 0 && (
          <>
            <Box className={classes.sectionHeader}>
              <StarIcon className={classes.sectionIcon} />
              <Typography variant="caption" className={classes.sectionTitle}>
                {t('conversationHistory.starred')}
              </Typography>
            </Box>
            <List dense className={classes.list}>
              {starredConversations.map(conversation => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={selectedConversationId === conversation.id}
                  onSelect={() => onSelectConversation(conversation)}
                  onToggleStar={() => onToggleStar(conversation.id)}
                  onDelete={() => onDelete(conversation.id)}
                  onRename={title => onRename(conversation.id, title)}
                />
              ))}
            </List>
          </>
        )}

        {/* Divider between starred and recent */}
        {starredConversations.length > 0 && recentConversations.length > 0 && (
          <Divider className={classes.divider} />
        )}

        {/* Recent conversations section */}
        {recentConversations.length > 0 && (
          <>
            <Box className={classes.sectionHeader}>
              <Typography variant="body2">
                {t('conversationHistory.recent')}
              </Typography>
            </Box>
            <List dense className={classes.list}>
              {recentConversations.map(conversation => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={selectedConversationId === conversation.id}
                  onSelect={() => onSelectConversation(conversation)}
                  onToggleStar={() => onToggleStar(conversation.id)}
                  onDelete={() => onDelete(conversation.id)}
                  onRename={title => onRename(conversation.id, title)}
                />
              ))}
            </List>
          </>
        )}
      </Box>
    </Box>
  );
};
